import type {
  StripeAccountDetails,
  StripeBookingDepositIntent,
  StripeConnectStatus,
  StripeDepositReconcileResult,
  StripePaymentPolicy,
  StripeSetupIntentResponse,
  StripeSetupIntentStatus,
  StripeTransaction,
  StripeTransactionMode,
  StripeTransactionSummary,
} from '../types/stripe.types';

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : {};
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function asNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((item) => asString(item)).filter(Boolean);
}

export function normalizeStripeConnectStatus(
  response: unknown,
  branchCode: string,
): StripeConnectStatus {
  const source = asRecord(response);

  return {
    branchCode: asString(source.branchCode) || branchCode,
    connectedAccountId: asString(source.connectedAccountId),
    onboardingUrl: asString(source.onboardingUrl),
    detailsSubmitted: source.detailsSubmitted === true,
    chargesEnabled: source.chargesEnabled === true,
    payoutsEnabled: source.payoutsEnabled === true,
    status: asString(source.status),
  };
}

export function normalizeStripeAccountDetails(
  response: unknown,
  branchCode: string,
): StripeAccountDetails {
  const source = asRecord(response);

  return {
    branchCode: asString(source.branchCode) || branchCode,
    connectedAccountId: asString(source.connectedAccountId),
    email: asString(source.email),
    businessName: asString(source.businessName),
    country: asString(source.country),
    defaultCurrency: asString(source.defaultCurrency),
    accountType: asString(source.accountType),
    detailsSubmitted: source.detailsSubmitted === true,
    chargesEnabled: source.chargesEnabled === true,
    payoutsEnabled: source.payoutsEnabled === true,
    status: asString(source.status),
    disabledReason: asString(source.disabledReason),
    currentlyDue: asStringList(source.currentlyDue),
    pastDue: asStringList(source.pastDue),
    pendingVerification: asStringList(source.pendingVerification),
  };
}

export function normalizeStripeTransaction(
  item: unknown,
): StripeTransaction | null {
  const source = asRecord(item);
  const depositCode = asString(source.depositCode);

  if (!depositCode) {
    return null;
  }

  const mode: StripeTransactionMode =
    asString(source.mode) === 'AUTHORIZE_ONLY' ? 'AUTHORIZE_ONLY' : 'PAY_NOW';

  const url = asString(source.url);
  const refundableAmountRaw = source.refundableAmount;

  return {
    depositCode,
    bookingCode: asString(source.bookingCode),
    branchCode: asString(source.branchCode),
    connectedAccountId: asString(source.connectedAccountId),
    paymentLinkId: asString(source.paymentLinkId),
    paymentIntentId: asString(source.paymentIntentId),
    ...(url ? { url } : {}),
    requestedAmount: asNumber(source.requestedAmount),
    capturedAmount: asNumber(source.capturedAmount),
    refundedAmount: asNumber(source.refundedAmount),
    ...(refundableAmountRaw !== undefined && refundableAmountRaw !== null
      ? { refundableAmount: asNumber(refundableAmountRaw) }
      : {}),
    currency: asString(source.currency) || 'eur',
    description: asString(source.description),
    mode,
    status: asString(source.status),
    failureMessage: asString(source.failureMessage),
    createdAt: asString(source.createdAt),
  };
}

export function normalizeStripeTransactionList(
  response: unknown,
): StripeTransaction[] {
  const payload = Array.isArray(response)
    ? response
    : Array.isArray(asRecord(response).content)
      ? (asRecord(response).content as unknown[])
      : [];

  return payload
    .map((item) => normalizeStripeTransaction(item))
    .filter((item): item is StripeTransaction => item !== null);
}

export function normalizeStripeTransactionSummary(
  response: unknown,
): StripeTransactionSummary {
  const source = asRecord(response);

  return {
    all: asNumber(source.all),
    succeeded: asNumber(source.succeeded),
    uncaptured: asNumber(source.uncaptured),
    failed: asNumber(source.failed),
  };
}

export function normalizeStripePaymentPolicy(
  response: unknown,
  branchCode: string,
): StripePaymentPolicy {
  const source = asRecord(response);

  return {
    branchCode: asString(source.branchCode) || branchCode,
    content: typeof source.content === 'string' ? source.content : '',
    version: asString(source.version),
    updatedAt: asString(source.updatedAt),
  };
}

export function normalizeStripeSetupIntent(
  response: unknown,
): StripeSetupIntentResponse {
  const source = asRecord(response);
  const status = asString(source.status);

  return {
    flowCode: asString(source.flowCode),
    clientSecret: asString(source.clientSecret) || undefined,
    publishableKey: asString(source.publishableKey) || undefined,
    status,
    verificationRequired: source.verificationRequired === true,
    reused: source.reused === true,
  };
}

export function normalizeStripeSetupIntentStatus(
  response: unknown,
  flowCode: string,
): StripeSetupIntentStatus {
  const source = asRecord(response);

  return {
    flowCode: asString(source.flowCode) || flowCode,
    status: asString(source.status),
  };
}

export function normalizeStripeBookingDepositIntent(
  response: unknown,
): StripeBookingDepositIntent {
  const source = asRecord(response);
  const mode: StripeTransactionMode =
    asString(source.mode) === 'PAY_NOW' ? 'PAY_NOW' : 'AUTHORIZE_ONLY';

  return {
    depositCode: asString(source.depositCode),
    bookingCode: asString(source.bookingCode),
    paymentIntentId: asString(source.paymentIntentId),
    clientSecret: asString(source.clientSecret),
    publishableKey: asString(source.publishableKey),
    amountInMinorUnits: asNumber(source.amountInMinorUnits),
    currency: asString(source.currency) || 'eur',
    mode,
  };
}

export function normalizeStripeDepositReconcile(
  response: unknown,
): StripeDepositReconcileResult {
  const source = asRecord(response);

  return {
    status: asString(source.status) || 'PROCESSING',
  };
}
